import { observer } from "mobx-react";
import React, { useEffect } from "react";
import {
  Modal,
  Button,
  Stack,
  TextInput,
  Text,
  Group,
  Paper,
  Alert,
} from "@mantine/core";
import {
  IconGift,
  IconCheck,
  IconAlertTriangle,
  IconRefresh,
  IconReceipt,
} from "@tabler/icons-react";
import { BOB_MODALS, BOB_PAGES } from "../../helpers/constants";
import { Bob } from "../../stores/Bob";

const CODE_LENGTH = 9; // 4 chars + dash + 4 chars

const PromoCodeModal = observer(({ bob }: { bob: Bob }) => {
  const isOpen = bob.openModal === BOB_MODALS.PROMO_CODE;
  const proposal = bob.selectedBobSponsorshipProposal;

  useEffect(() => {
    if (isOpen && !proposal) {
      bob.createAndSelectNewBobSponsorshipProposal();
    }
  }, [isOpen]);

  const handleClose = () => {
    bob.deleteAllBobSponsorshipProposals();
    bob.setOpenModal(null);
  };

  if (!isOpen || !proposal) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    proposal.setProposedCode(e.currentTarget.value);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    proposal.setProposedCode(e.clipboardData.getData("text"));
  };

  const handleStartOver = () => {
    proposal.setProposedCode("");
  };

  const handleOpenTransactions = () => {
    bob.deleteAllBobSponsorshipProposals();
    bob.setOpenModal(null);
    bob.setOpenPage(BOB_PAGES.TRANSACTIONS_LIST);
  };

  return (
    <Modal opened={isOpen} onClose={handleClose} title="Promo Code">
      <Paper withBorder shadow="md" p={24} radius="md">
        <Stack>
          <TextInput
            label="Enter your promo code"
            placeholder="XXXX-XXXX"
            value={proposal.proposedCode}
            onChange={handleChange}
            onPaste={handlePaste}
            error={proposal.userFacingCodeErrorMessage}
            maxLength={CODE_LENGTH}
            disabled={bob.areWeWaitingOnRails}
            autoFocus
            styles={{
              input: {
                fontFamily: "monospace",
                fontSize: 20,
                letterSpacing: 3,
                textAlign: "center",
                textTransform: "uppercase",
              },
            }}
          />

          {!proposal.userFacingCodeErrorMessage &&
            proposal.proposedCode.length > 0 &&
            !proposal.isCodeReady && (
              <Text size="sm" c="dimmed">
                {proposal.charsRemaining} character
                {proposal.charsRemaining !== 1 ? "s" : ""} remaining
              </Text>
            )}

          {proposal.isCodeReady &&
            !proposal.serverErrorMessage &&
            !proposal.serverSuccessMessage && (
              <Text size="sm" c="green">
                <IconCheck size={14} style={{ verticalAlign: "middle" }} /> Code
                format looks good!
              </Text>
            )}

          {proposal.serverErrorMessage && (
            <Alert
              icon={<IconAlertTriangle size={18} />}
              color="red"
              radius="md"
            >
              {proposal.serverErrorMessage}
            </Alert>
          )}

          {proposal.serverSuccessMessage && (
            <Alert icon={<IconCheck size={18} />} color="green" radius="md">
              {proposal.serverSuccessMessage}
            </Alert>
          )}

          <Group justify="flex-end" mt="md">
            {proposal.serverSuccessMessage ? (
              <>
                <Button
                  variant="outline"
                  leftSection={<IconRefresh size={18} />}
                  onClick={handleStartOver}
                >
                  Start Over
                </Button>
                <Button
                  leftSection={<IconReceipt size={18} />}
                  onClick={handleOpenTransactions}
                >
                  Transactions
                </Button>
              </>
            ) : proposal.serverErrorMessage ? (
              <>
                <Button
                  variant="outline"
                  leftSection={<IconRefresh size={18} />}
                  onClick={handleStartOver}
                >
                  Start Over
                </Button>
                <Button variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                {proposal.isCodeReady && (
                  <Button
                    leftSection={<IconGift size={18} />}
                    onClick={() => proposal.checkPromoCode()}
                    disabled={bob.areWeWaitingOnRails}
                    loading={bob.areWeWaitingOnRails}
                  >
                    Check Code
                  </Button>
                )}
              </>
            )}
          </Group>
        </Stack>
      </Paper>
    </Modal>
  );
});

export default PromoCodeModal;
